import React from 'react';
import { Box, Typography, IconButton, LinearProgress, Tooltip, useTheme } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import { Layers, Square } from 'lucide-react';
import { tokens } from '../../../../../theme';
import SectionStepper from './SectionStepper';

/**
 * Shared nav toolbar for test-taking and results right panel.
 *
 * Props:
 *   sections, currentSectionIndex, onSectionNavigate, answers (Map for stepper)
 *   viewMode ('single'|'all'), onToggleViewMode
 *   currentQuestionIndex, sectionTotal, isFirst, isLast, onPrev, onNext
 *   onProgressClick(questionIndex)
 *   flaggedQuestions — [{id, index}] for flag markers (optional)
 *   onFlagClick(index) (optional)
 *   questionResults — [{status: 'correct'|'wrong'|'pending'|'unanswered'}] per question (optional, results mode)
 */
const QuestionNav = ({
    sections, currentSectionIndex, onSectionNavigate, answers,
    viewMode, onToggleViewMode,
    currentQuestionIndex, sectionTotal, isFirst, isLast, onPrev, onNext,
    onProgressClick, flaggedQuestions, onFlagClick,
    questionResults,
    useQuestionUi = false,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const progress = sectionTotal > 0 ? ((currentQuestionIndex + 1) / sectionTotal) * 100 : 0;

    const renderProgressBar = () => {
        const isResults = questionResults && questionResults.length > 0;
        const segWidth = sectionTotal > 0 ? 100 / sectionTotal : 0;
        const resultColor = { correct: '#4caf50', partial: '#f59e0b', wrong: '#f44336', graded: colors.blueAccent[500], pending: colors.grey[600], unanswered: colors.grey[700] };

        return (
            <Box sx={{ flex: 1, position: 'relative' }}>
                {/* Flag markers */}
                {flaggedQuestions?.map(({ id, index }) => {
                    const left = sectionTotal > 1 ? ((index + 0.5) / sectionTotal) * 100 : 50;
                    return (
                        <FlagIcon key={id} onClick={(e) => { e.stopPropagation(); onFlagClick?.(index); }}
                            sx={{ position: 'absolute', top: '50%', left: `${left}%`, transform: 'translate(-50%, -100%)',
                                fontSize: 16, color: '#f59e0b', cursor: 'pointer', zIndex: 3 }}
                        />
                    );
                })}
                {/* Segmented bar */}
                <Box
                    sx={{ height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', cursor: 'pointer', bgcolor: colors.grey[800], position: 'relative' }}
                    onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const pct = (e.clientX - rect.left) / rect.width;
                        onProgressClick?.(Math.max(0, Math.min(Math.floor(pct * sectionTotal), sectionTotal - 1)));
                    }}
                >
                    {Array.from({ length: sectionTotal }, (_, idx) => {
                        let bgcolor;
                        if (isResults) {
                            bgcolor = resultColor[questionResults[idx]?.status] || colors.grey[700];
                        } else {
                            // grey=unanswered, blue=answered, current marker handles position
                            const section = sections[currentSectionIndex];
                            const q = section?.v2_test_questions?.[idx];
                            const hasAnswer = q && answers?.get(q.id)?.answer_text;
                            bgcolor = hasAnswer ? colors.blueAccent[500] : colors.grey[700];
                        }
                        return (
                            <Tooltip key={idx} title={`Q${idx + 1}`} placement="top" arrow>
                                <Box sx={{
                                    width: `${segWidth}%`,
                                    bgcolor,
                                    borderRight: idx < sectionTotal - 1 ? `0.5px solid ${colors.grey[900]}` : 'none',
                                    transition: 'opacity 0.15s',
                                    opacity: idx === currentQuestionIndex ? 1 : 0.75,
                                    '&:hover': { opacity: 1 },
                                }} />
                            </Tooltip>
                        );
                    })}
                    {/* Current position marker */}
                    <Box sx={{
                        position: 'absolute',
                        top: -3, bottom: -3,
                        left: `${((currentQuestionIndex + 0.5) / sectionTotal) * 100}%`,
                        transform: 'translateX(-50%)',
                        width: 10, bgcolor: 'text.primary', borderRadius: 1, zIndex: 2,
                        transition: 'left 0.25s ease-in-out',
                    }} />
                </Box>
            </Box>
        );
    };

    return (
        <>
            {/* Section stepper + lightbulb + view toggle */}
            <Box sx={{ px: 1, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                {sections.length > 1 && (
                    <SectionStepper
                        sections={sections}
                        currentIndex={currentSectionIndex}
                        onNavigate={onSectionNavigate}
                        answers={answers}
                    />
                )}
                <Box sx={{ flex: 1 }} />
                {(viewMode === 'single' || useQuestionUi) && (
                    <Tooltip title={viewMode === 'single' ? 'Use ← → arrow keys to navigate' : 'Use ↑ ↓ arrow keys to navigate'} placement="bottom">
                        <TipsAndUpdatesOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'default' }} />
                    </Tooltip>
                )}
                <Tooltip title={viewMode === 'single' ? 'Show all questions' : 'Show one at a time'} placement="bottom">
                    <IconButton size="small" onClick={onToggleViewMode} sx={{ color: 'text.secondary', p: 0.5 }}>
                        {viewMode === 'single' ? <Layers size={18} /> : <Square size={18} />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Progress bar + chevrons — single mode only */}
            {viewMode === 'single' && (
                <Box sx={{ px: 2, pb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 0.3 }}>
                        Q{currentQuestionIndex + 1} of {sectionTotal}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="small" disabled={isFirst} onClick={onPrev} sx={{ color: 'text.primary', p: 0.5 }}>
                            <NavigateBeforeIcon fontSize="small" />
                        </IconButton>
                        {renderProgressBar()}
                        <IconButton size="small" disabled={isLast} onClick={onNext} sx={{ color: 'text.primary', p: 0.5 }}>
                            <NavigateNextIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default QuestionNav;
